import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { usePreviewImage } from '@euler/app/components/media/ImageViewerContext';
import { Typography } from '@euler/app/components/typography';
import { DateTimePicker } from '@euler/components/DateTimePicker';
import {
  kApdmBlockReasonLabels,
  kApdmCampaignKindLabels,
  kApdmPreviewWarningLabels,
} from '@euler/functions/apdm/campaignLabels';
import {
  apdmCanSendImmediately,
  apdmNextSendingAt,
  apdmSendingTimeText,
} from '@euler/functions/apdm/campaignSending';
import { apdmPlanCardSendingPolicy } from '@euler/functions/apdm/planSendingPolicy';
import { canManageApdmImports } from '@euler/functions/apdmAccess';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useUserProfileInfo } from '@euler/functions/user/useUserProfileInfo';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import {
  ApdmAudienceCandidate,
  ApdmBuildCardKind,
  ApdmBuildStep,
  ApdmCampaignDailyWindow,
  ApdmCampaignOutcome,
  ApdmCouponCampaignStatus,
  ApdmPlanFirstSend,
  ApdmPlanLaunchResult,
  ApdmSendingPolicy,
  RpaOutboundMessage,
  apdmDailyWindowText,
  parseApdmDailyWindow,
  sameDailyWindow,
} from '@euler/model/apdm';
import { useServiceFactory } from '@euler/services/factory';
import dayjs from 'dayjs';
import {
  MessageCircle,
  Rocket,
  Ticket,
  TriangleAlert,
  Users,
} from 'lucide-react-native';
import { ReactNode, memo, useEffect, useState } from 'react';
import { Image, Pressable, View } from 'react-native';
import { useAiChatActions, useAiPlanStepOpen } from '../chat/AiChatActions';
import {
  AudienceCounts,
  CardLoading,
  CardNotice,
  ChatCard,
  ChatCardBadge,
} from '../components/ChatCard';
import { PrimaryButton, SecondaryButton } from '../components/PrimaryButton';
import { couponItemText, couponPeriodText } from '../components/couponText';
import { aiColors, tint } from '../theme';
import { SendingPolicyPanel } from './SendingPolicyPanel';
import { isAiMessageId, usePlanCard } from './usePlanCard';

/** The plan as it will go out: who, what, when; launched by a tap. */
export const PlanCampaignCard = memo(
  ({ messageId, streaming }: { messageId?: string; streaming?: boolean }) => {
    const profile = useUserProfileInfo();
    // A reply that is still arriving has no stored id to read by yet.
    if (streaming || !isAiMessageId(messageId)) return null;
    if (!canManageApdmImports(profile ?? null))
      return <CardNotice text="当前账号无法查看这个活动。" />;
    return <PlanCampaignBody key={messageId} messageId={messageId} />;
  },
);

function PlanCampaignBody({ messageId }: { messageId: string }) {
  const { rpx } = useResponsiveContext();
  const navigation = useAppNavigation();
  const { apdmCampaignService } = useServiceFactory();
  const chat = useAiChatActions();
  const open = useAiPlanStepOpen(ApdmBuildStep.Launch);
  const confirm = useConfirm();
  const state = usePlanCard(messageId, ApdmBuildCardKind.Campaign);
  const [reasons, setReasons] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const [launched, setLaunched] = useState<
    (ApdmPlanLaunchResult & { firstSendText: string }) | null
  >(null);
  const [firstSend, setFirstSend] = useState(ApdmPlanFirstSend.AsScheduled);
  const [adjusting, setAdjusting] = useState(false);
  // Null keeps the hours the card showed.
  const [dailyWindow, setDailyWindow] =
    useState<ApdmCampaignDailyWindow | null>(null);
  const [picking, setPicking] = useState<keyof ApdmCampaignDailyWindow | null>(
    null,
  );
  const [windowError, setWindowError] = useState<string | null>(null);
  // Null keeps the policy the card showed.
  const [pickedPolicy, setPickedPolicy] = useState<ApdmSendingPolicy | null>(
    null,
  );
  const [pickingPolicy, setPickingPolicy] = useState(false);

  if (state.kind === 'loading')
    return (
      <ChatCard icon={Users} title="活动预览" tinted>
        <CardLoading label="正在读取活动预览…" />
      </ChatCard>
    );
  if (
    state.kind === 'missing' ||
    state.view.card !== ApdmBuildCardKind.Campaign
  )
    return (
      <ChatCard icon={Users} title="活动预览">
        <CardNotice text="没有读取到这份活动预览，请稍后重新打开对话。" />
      </ChatCard>
    );

  const preview = state.view.campaign;
  const view = preview.definition;
  const counts = preview.counts;
  const coupon = preview.coupon;
  const composed = preview.composedCoupon;
  const sample = preview.samples.find(
    (entry): entry is Extract<typeof entry, { kind: 'ready' }> =>
      entry.kind === 'ready',
  );
  const variants = (preview.variantExamples?.length ?? 0) - 1;
  // Only the latest reply's card launches; an older one shows what it
  // showed, and a later reply has a newer card.
  const launchable = open && !launched;
  const shownWindow = dailyWindow ?? view.schedule.dailyWindow;
  const sendingSchedule = { ...view.schedule, dailyWindow: shownWindow };
  // Read at each render: a card left open past the window's opening no
  // longer offers to send early.
  const canSendImmediately = apdmCanSendImmediately(sendingSchedule);
  const immediately =
    canSendImmediately && firstSend === ApdmPlanFirstSend.Immediately;
  const nextSendingAt = apdmNextSendingAt(sendingSchedule);
  const firstSendText = immediately
    ? '立即'
    : nextSendingAt
    ? `${apdmSendingTimeText(nextSendingAt)} `
    : null;
  const sendingPolicy = apdmPlanCardSendingPolicy(
    view.policy.outReaching,
    preview.defaultSendingPolicy,
    pickedPolicy,
  );
  const windowChanged = !sameDailyWindow(
    shownWindow,
    view.schedule.dailyWindow,
  );
  const pickClock = (date: Date | undefined) => {
    const end = picking;
    setPicking(null);
    if (!end || !date) return;
    const next = parseApdmDailyWindow({
      ...shownWindow,
      [end]: dayjs(date).format('HH:mm'),
    });
    setWindowError(next ? null : '结束时间要晚于开始时间。');
    if (next) setDailyWindow(next);
  };

  const launch = async () => {
    if (chat.busy || launching) return;
    const accepted = await confirm({
      title: '启动活动',
      message: `将向约 ${counts.known + counts.unconfirmed} 位客户发送消息${
        composed
          ? `，并创建优惠券活动「${
              composed.coupon.title ?? composed.coupon.name
            }」`
          : coupon?.status === ApdmCouponCampaignStatus.Draft
          ? `，并发布优惠券活动「${coupon.title}」`
          : ''
      }。${
        firstSendText ? `首批消息${firstSendText}发出，` : ''
      }之后每天 ${apdmDailyWindowText(shownWindow)} 发送。`,
      confirmButtonText: '启动',
    });
    if (!accepted) return;
    setLaunching(true);
    setLaunchError(null);
    try {
      const result = await apdmCampaignService.launchPlan({
        messageId,
        firstSend: immediately
          ? ApdmPlanFirstSend.Immediately
          : ApdmPlanFirstSend.AsScheduled,
        ...(windowChanged ? { dailyWindow: shownWindow } : {}),
        ...(sendingPolicy.override
          ? { sendingPolicy: sendingPolicy.override }
          : {}),
      });
      setLaunched({ ...result, firstSendText: firstSendText ?? '' });
      // The conversation goes on about the campaign it launched.
      chat.send('活动启动了', {
        answer: {
          step: ApdmBuildStep.Launch,
          campaignId: result.campaignId,
          outcome: ApdmCampaignOutcome.Launched,
        },
      });
    } catch (failure) {
      console.error('APDM plan launch failed', failure);
      setLaunchError(
        '没有启动成功。活动方案可能在这之后改过，可以让我重新生成预览。',
      );
    } finally {
      setLaunching(false);
    }
  };

  const detail = launched
    ? {
        label: '查看活动详情',
        onPress: () =>
          navigation.navigate('ApdmCampaign', {
            campaignId: launched.campaignId,
          }),
      }
    : null;

  return (
    <ChatCard
      icon={Users}
      tinted
      title={view.title || kApdmCampaignKindLabels[view.kind]}
      subtitle={[
        kApdmCampaignKindLabels[view.kind],
        coupon?.title ?? composed?.coupon.title,
      ]
        .filter(part => part && part !== view.title)
        .join(' · ')}
      badge={
        <ChatCardBadge
          label={launched ? '已启动' : '待启动'}
          color={launched ? aiColors.green : aiColors.primary}
        />
      }
      detail={detail}
    >
      <Section title="客户">
        <AudienceCounts counts={counts} />
        <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
          {dayjs(preview.asOf).format('M月D日 HH:mm')} 统计
        </Typography.Text>
        {counts.exclusionReasons.length > 0 && (
          <Pressable
            accessibilityRole="button"
            accessibilityState={{ expanded: reasons }}
            onPress={() => setReasons(value => !value)}
          >
            <Typography.Text medium size={rpx(12)} color={aiColors.primary}>
              {reasons ? '收起排除原因' : '查看排除原因'}
            </Typography.Text>
          </Pressable>
        )}
        {reasons &&
          counts.exclusionReasons.map(entry => (
            <Row
              key={entry.reason}
              label={kApdmBlockReasonLabels[entry.reason]}
              value={`${entry.count}`}
            />
          ))}
      </Section>
      {preview.samples.length > 0 && (
        <Section title="客户示例">
          <View
            style={{
              borderWidth: 1,
              borderColor: aiColors.border,
              borderRadius: rpx(10),
            }}
          >
            {preview.samples.slice(0, 3).map((entry, index) => (
              <View
                key={entry.candidate.licensePlateNo}
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  gap: rpx(8),
                  paddingHorizontal: rpx(12),
                  paddingVertical: rpx(8),
                  borderTopWidth: index ? 1 : 0,
                  borderTopColor: aiColors.border,
                }}
              >
                <Typography.Text
                  medium
                  size={rpx(12)}
                  color={aiColors.foreground}
                >
                  {entry.candidate.licensePlateNo}
                </Typography.Text>
                <Typography.Text
                  size={rpx(12)}
                  color={aiColors.mutedForeground}
                  numberOfLines={1}
                  style={{ flexShrink: 1 }}
                >
                  {sampleFacts(entry.candidate.facts)}
                </Typography.Text>
              </View>
            ))}
          </View>
        </Section>
      )}
      {composed ? (
        <Section title="优惠券活动（启动时创建）">
          <View
            style={{
              padding: rpx(12),
              borderRadius: rpx(12),
              borderWidth: 1,
              borderColor: aiColors.border,
              gap: rpx(6),
            }}
          >
            <Typography.Text medium size={rpx(14)} color={aiColors.foreground}>
              {composed.coupon.title ?? composed.coupon.name}
            </Typography.Text>
            {composed.coupon.items.map((item, index) => (
              <Typography.Text
                key={index}
                size={rpx(12)}
                color={aiColors.foreground}
              >
                · {item.label}
              </Typography.Text>
            ))}
          </View>
        </Section>
      ) : null}
      {coupon ? (
        <Section
          title="优惠券活动"
          action={
            launchable
              ? {
                  label: '更换',
                  onPress: () => chat.send('换一个优惠券活动'),
                }
              : undefined
          }
        >
          <View
            style={{
              padding: rpx(12),
              borderRadius: rpx(12),
              borderWidth: 1,
              borderColor: aiColors.border,
              gap: rpx(6),
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: rpx(10),
              }}
            >
              <View
                style={{
                  width: rpx(32),
                  height: rpx(32),
                  borderRadius: rpx(9),
                  backgroundColor: aiColors.primary,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Ticket size={16} color="#fff" />
              </View>
              <View style={{ flex: 1 }}>
                <Typography.Text
                  medium
                  size={rpx(14)}
                  color={aiColors.foreground}
                >
                  {coupon.title}
                </Typography.Text>
                <Typography.Text
                  size={rpx(11)}
                  color={aiColors.mutedForeground}
                >
                  {couponPeriodText(coupon)}
                  {coupon.stockLeft == null
                    ? ''
                    : ` · 剩余 ${coupon.stockLeft} 份`}
                </Typography.Text>
              </View>
            </View>
            {coupon.items.map((item, index) => (
              <Typography.Text
                key={index}
                size={rpx(12)}
                color={aiColors.foreground}
              >
                · {couponItemText(item)}
              </Typography.Text>
            ))}
          </View>
        </Section>
      ) : null}
      {!sample && (
        <Section title="消息预览">
          <CardNotice
            text={
              view.variants.length
                ? '暂时生成不了消息预览，稍后再看一次。'
                : '还没有消息文案。说说想对客户说什么，或让我写几条。'
            }
          />
        </Section>
      )}
      {sample && (
        <Section title="消息预览">
          <View
            style={{
              padding: rpx(12),
              borderRadius: rpx(12),
              backgroundColor: aiColors.muted,
              gap: rpx(8),
            }}
          >
            {sample.messages.map((message, index) => (
              <OutboundMessage
                key={index}
                message={message}
                poster={!!coupon}
              />
            ))}
          </View>
          {variants > 0 && (
            <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
              另有 {variants} 条文案轮流发送
            </Typography.Text>
          )}
        </Section>
      )}
      <Section title="发送计划">
        {launchable ? (
          <>
            <ClockRow
              label="每日开始"
              value={shownWindow.from}
              disabled={chat.busy || launching}
              onPress={() => setPicking('from')}
            />
            <ClockRow
              label="每日结束"
              value={shownWindow.to}
              disabled={chat.busy || launching}
              onPress={() => setPicking('to')}
            />
            {windowError && <CardNotice tone="danger" text={windowError} />}
            <DateTimePicker
              // The web picker reads `date` only when it mounts.
              key={picking ?? 'closed'}
              isVisible={picking != null}
              mode="time"
              title={picking === 'to' ? '每日结束时间' : '每日开始时间'}
              date={clockDate(picking ? shownWindow[picking] : '09:00')}
              onConfirm={pickClock}
              onCancel={() => setPicking(null)}
              confirmTextIOS="确认"
              cancelTextIOS="取消"
            />
          </>
        ) : (
          <Row label="每日时段" value={apdmDailyWindowText(shownWindow)} />
        )}
        <Row
          label="每日上限"
          value={
            view.schedule.dailyCap == null
              ? '不限'
              : `${view.schedule.dailyCap} 条`
          }
        />
        <Row
          label="活动日期"
          value={
            !view.schedule.startAt
              ? '尚未设置'
              : view.schedule.endAt
              ? `${dayjs(view.schedule.startAt).format('M月D日')} – ${dayjs(
                  view.schedule.endAt,
                ).format('M月D日')}`
              : `${dayjs(view.schedule.startAt).format('M月D日')}起`
          }
        />
        {launchable && sendingPolicy.pickable ? (
          <>
            <ClockRow
              label="发送方式"
              value={sendingPolicy.text}
              disabled={chat.busy || launching}
              onPress={() => setPickingPolicy(true)}
            />
            <SendingPolicyPanel
              visible={pickingPolicy}
              value={sendingPolicy.selected}
              defaultPolicy={sendingPolicy.defaultPolicy}
              onClose={() => setPickingPolicy(false)}
              onSelect={policy => {
                setPickingPolicy(false);
                setPickedPolicy(policy);
              }}
            />
          </>
        ) : (
          <Row label="发送方式" value={sendingPolicy.text} />
        )}
        {launchable && canSendImmediately ? (
          <View style={{ gap: rpx(8) }}>
            <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
              首批消息
            </Typography.Text>
            {/* One row; a narrow card wraps the second option under it. */}
            <View
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                columnGap: rpx(20),
                rowGap: rpx(8),
              }}
            >
              <RadioOption
                label="立即发送"
                selected={immediately}
                disabled={chat.busy || launching}
                onPress={() => setFirstSend(ApdmPlanFirstSend.Immediately)}
              />
              <RadioOption
                label={`按时段发送（${apdmSendingTimeText(
                  nextSendingAt ?? dayjs(),
                )}）`}
                selected={!immediately}
                disabled={chat.busy || launching}
                onPress={() => setFirstSend(ApdmPlanFirstSend.AsScheduled)}
              />
            </View>
          </View>
        ) : launchable ? (
          <Row
            label="首批消息"
            value={
              nextSendingAt
                ? apdmSendingTimeText(nextSendingAt)
                : '活动结束前已没有发送时段'
            }
          />
        ) : null}
      </Section>
      {preview.warnings.length > 0 && (
        <View
          style={{
            flexDirection: 'row',
            gap: rpx(8),
            padding: rpx(12),
            borderRadius: rpx(10),
            borderWidth: 1,
            borderColor: tint(aiColors.amber, 0.4),
            backgroundColor: tint(aiColors.amber, 0.1),
          }}
        >
          <TriangleAlert size={16} color={aiColors.amber} />
          <View style={{ flex: 1, gap: rpx(2) }}>
            {preview.warnings.map(warning => (
              <Typography.Text
                key={warning}
                size={rpx(12)}
                lineHeight={rpx(18)}
                color={aiColors.foreground}
              >
                {kApdmPreviewWarningLabels[warning]}
              </Typography.Text>
            ))}
          </View>
        </View>
      )}
      {launchError && <CardNotice tone="danger" text={launchError} />}
      {launched ? (
        <CardNotice
          text={`活动已启动，${
            launched.firstSendText
              ? `首批消息${launched.firstSendText}发出，之后`
              : ''
          }在每日时段内发送。可在活动详情里查看进度。`}
        />
      ) : launchable ? (
        <>
          <PrimaryButton
            title="启动活动"
            icon={Rocket}
            loading={launching}
            disabled={chat.busy || launching}
            onPress={() => void launch()}
          />
          <SecondaryButton
            title="继续调整"
            icon={MessageCircle}
            disabled={chat.busy || launching}
            onPress={() => setAdjusting(value => !value)}
          />
          {adjusting ? (
            <View
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: rpx(8),
              }}
            >
              {kAdjustments.map(([label, request]) => (
                <Pressable
                  key={label}
                  accessibilityRole="button"
                  disabled={chat.busy}
                  onPress={() => chat.send(request)}
                  style={({ pressed }) => ({
                    paddingHorizontal: rpx(12),
                    paddingVertical: rpx(6),
                    borderRadius: rpx(16),
                    borderWidth: 1,
                    borderColor: aiColors.border,
                    backgroundColor: aiColors.card,
                    opacity: pressed ? 0.7 : 1,
                  })}
                >
                  <Typography.Text size={rpx(12)} color={aiColors.foreground}>
                    {label}
                  </Typography.Text>
                </Pressable>
              ))}
            </View>
          ) : null}
        </>
      ) : null}
    </ChatCard>
  );
}

export const Section = memo(
  ({
    title,
    action,
    children,
  }: {
    title: string;
    action?: { label: string; onPress: () => void };
    children: ReactNode;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View style={{ gap: rpx(8) }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          <Typography.Text medium size={rpx(12)} color={aiColors.foreground}>
            {title}
          </Typography.Text>
          {action && (
            <Pressable accessibilityRole="button" onPress={action.onPress}>
              <Typography.Text medium size={rpx(12)} color={aiColors.primary}>
                {action.label}
              </Typography.Text>
            </Pressable>
          )}
        </View>
        {children}
      </View>
    );
  },
);

export const Row = memo(
  ({ label, value }: { label: string; value: string }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
        <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
          {label}
        </Typography.Text>
        <Typography.Text medium size={rpx(12)} color={aiColors.foreground}>
          {value}
        </Typography.Text>
      </View>
    );
  },
);

const OutboundMessage = memo(
  ({ message, poster }: { message: RpaOutboundMessage; poster: boolean }) => {
    const { rpx } = useResponsiveContext();
    switch (message.kind) {
      case 'text':
        return (
          <View
            style={{
              alignSelf: 'flex-start',
              maxWidth: '88%',
              paddingHorizontal: rpx(12),
              paddingVertical: rpx(8),
              borderRadius: rpx(12),
              borderTopLeftRadius: rpx(4),
              backgroundColor: aiColors.accent,
            }}
          >
            <Typography.Text
              size={rpx(12)}
              lineHeight={rpx(19)}
              color={aiColors.accentForeground}
            >
              {message.text}
            </Typography.Text>
          </View>
        );
      case 'image':
        return (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: rpx(12),
              padding: rpx(8),
              borderRadius: rpx(10),
              backgroundColor: aiColors.card,
            }}
          >
            <PosterThumb url={message.url} />
            <Typography.Text
              size={rpx(12)}
              color={aiColors.mutedForeground}
              style={{ flex: 1 }}
            >
              {poster ? '每位客户收到的海报都带有自己的核销二维码。' : '图片'}
            </Typography.Text>
          </View>
        );
      case 'link':
      case 'miniprogram':
        return (
          <View
            style={{
              padding: rpx(10),
              borderRadius: rpx(10),
              backgroundColor: aiColors.card,
              gap: rpx(2),
            }}
          >
            <Typography.Text medium size={rpx(12)} color={aiColors.foreground}>
              {message.title}
            </Typography.Text>
            <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
              {message.kind === 'link' ? message.desc ?? '链接' : '小程序'}
            </Typography.Text>
          </View>
        );
      case 'video':
        return (
          <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
            视频
          </Typography.Text>
        );
    }
  },
);

export function sampleFacts(
  facts: Pick<
    ApdmAudienceCandidate['facts'],
    'vehicleBrandModel' | 'lastVisitAt'
  >,
): string {
  const days = facts.lastVisitAt
    ? dayjs().diff(dayjs(facts.lastVisitAt), 'day')
    : null;
  return [facts.vehicleBrandModel, days == null ? null : `${days} 天未到店`]
    .filter(Boolean)
    .join(' · ');
}

/** Today at an `HH:mm` clock: the time picker needs a whole date. */
function clockDate(clock: string): Date {
  const [hours, minutes] = clock.split(':').map(Number);
  return dayjs().hour(hours).minute(minutes).second(0).millisecond(0).toDate();
}

/** One choice of a radio group. */
const RadioOption = memo(
  ({
    label,
    selected,
    disabled,
    onPress,
  }: {
    label: string;
    selected: boolean;
    disabled: boolean;
    onPress: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <Pressable
        accessibilityRole="radio"
        accessibilityState={{ checked: selected, disabled }}
        disabled={disabled}
        onPress={onPress}
        style={({ pressed }) => ({
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(8),
          opacity: disabled ? 0.6 : pressed ? 0.7 : 1,
        })}
      >
        <View
          style={{
            width: rpx(16),
            height: rpx(16),
            borderRadius: rpx(8),
            borderWidth: 1.5,
            borderColor: selected ? aiColors.primary : aiColors.border,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {selected ? (
            <View
              style={{
                width: rpx(8),
                height: rpx(8),
                borderRadius: rpx(4),
                backgroundColor: aiColors.primary,
              }}
            />
          ) : null}
        </View>
        <Typography.Text size={rpx(12)} color={aiColors.foreground}>
          {label}
        </Typography.Text>
      </Pressable>
    );
  },
);

/** A row the user taps to pick a value of the campaign's sending plan. */
const ClockRow = memo(
  ({
    label,
    value,
    disabled,
    onPress,
  }: {
    label: string;
    value: string;
    disabled: boolean;
    onPress: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={`${label} ${value}`}
        disabled={disabled}
        onPress={onPress}
        style={({ pressed }) => ({
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          opacity: disabled ? 0.6 : pressed ? 0.7 : 1,
        })}
      >
        <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
          {label}
        </Typography.Text>
        <Typography.Text medium size={rpx(12)} color={aiColors.primary}>
          {value}
        </Typography.Text>
      </Pressable>
    );
  },
);

/**
 * What the user may go back to from the finished plan, as they would say
 * it; the planner reopens that step and walks it again.
 */
const kAdjustments: [label: string, request: string][] = [
  ['调整客户', '我想调整一下目标客户'],
  ['调整时间', '我想调整一下活动时间'],
  ['调整优惠', '我想换一下优惠'],
  ['调整文案', '我想改一下文案'],
];

/** Posters are portrait; until the image says otherwise. */
const kPosterAspect = 3 / 4;

/**
 * A message image at a fixed height and its own width, so a poster shows
 * whole; a tap opens it full size.
 */
function PosterThumb({ url }: { url: string }) {
  const { rpx } = useResponsiveContext();
  const preview = usePreviewImage();
  const [aspect, setAspect] = useState(kPosterAspect);
  useEffect(() => {
    let active = true;
    Image.getSize(
      url,
      (width, height) => {
        if (active && width > 0 && height > 0) setAspect(width / height);
      },
      () => undefined,
    );
    return () => {
      active = false;
    };
  }, [url]);
  const height = rpx(112);
  return (
    <Pressable
      accessibilityRole="imagebutton"
      accessibilityLabel="查看大图"
      onPress={() => preview({ imageUrls: [url] })}
    >
      <Image
        source={{ uri: url }}
        resizeMode="cover"
        style={{
          width: height * aspect,
          height,
          borderRadius: rpx(8),
          backgroundColor: aiColors.muted,
        }}
      />
    </Pressable>
  );
}
